import {
  Component,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ErrorInfo,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, CheckCircle2, Info, Inbox, Loader2, X, XCircle } from 'lucide-react';
import { healthToken } from '@itr/shared';

/* ------------------------------------------------------------------ */
/* Health & badges                                                     */
/* ------------------------------------------------------------------ */

/* Kept in step with `.health-tooltip` in global.css — the placement maths has
   to know the box's size before the browser has laid it out. */
const TIP_MAX_W = 320;
const TIP_EST_H = 90;

/**
 * Health indicator.
 *
 * Carries three redundant channels — colour, dot *shape*, and the word itself —
 * so it survives colour blindness, greyscale printing and a washed-out
 * projector in a NOC. Colour alone is never the encoding.
 *
 * `reason` is the resource's `statusMessage` — *why* it is in this state. A
 * degraded row is useless without it ("degraded how?"), but the reason is a
 * sentence and the indicator is a chip, so it hangs off a hover/focus tooltip
 * instead of widening every table column that shows health.
 */
/**
 * Reachability at a glance, for the left of a resource's name wherever one
 * is listed: green when the device answers (its own check, or — when that
 * check fails — ping, any access port, or a MikroTik next to it; the
 * collector only reports `down` once every one of those is silent), red
 * when nothing reaches it, hollow grey when it hasn't been checked or is
 * in maintenance. Just the dot: the full health state with its reason is
 * `Health` / `ResourceHealth`.
 */
export function ReachDot({ health, size }: { health: string; size?: number }) {
  const reach = health === 'up' || health === 'degraded' ? 'yes' : health === 'down' ? 'no' : 'unknown';
  const text =
    reach === 'yes'
      ? 'Reachable — answers its check, ping or an access port'
      : reach === 'no'
        ? 'Unreachable — no answer on ping, any access port, or through a nearby MikroTik'
        : health === 'maintenance'
          ? 'In maintenance — reachability not reported'
          : 'Not checked yet';
  return (
    <span
      className="reach-dot"
      data-reach={reach}
      role="img"
      aria-label={text}
      title={text}
      style={size ? { width: size, height: size } : undefined}
    />
  );
}

export function Health({
  health,
  label = true,
  reason = null,
  body,
}: {
  health: string;
  label?: boolean;
  reason?: string | null;
  /** Replaces the plain reason text in the tooltip — for content with its own
   *  controls (see `ResourceHealth`). The tooltip then stays open while the
   *  pointer is over it, so those controls can be reached. */
  body?: ReactNode;
}) {
  const text = reason?.trim() ? reason.trim() : null;
  const ref = useRef<HTMLSpanElement>(null);
  const tipId = useId();
  const [tip, setTip] = useState<{ x: number; y: number; flip: boolean } | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const interactive = body !== undefined;

  // Measured off the trigger when it opens and drawn through a portal in the
  // viewport's own coordinates: the map tree and most tables sit inside
  // `overflow: auto` boxes, which would clip a tooltip positioned in flow.
  const open = useCallback(() => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    const rect = ref.current?.getBoundingClientRect();
    if (!rect || !text) return;
    const flip = rect.bottom + TIP_EST_H > window.innerHeight;
    setTip({
      x: Math.min(Math.max(rect.left, 8), Math.max(window.innerWidth - TIP_MAX_W - 8, 8)),
      y: flip ? rect.top - 8 : rect.bottom + 8,
      flip,
    });
  }, [text]);
  const close = useCallback(() => setTip(null), []);
  // With controls inside, leaving the chip must not close the tooltip before
  // the pointer can cross the gap onto it.
  const closeSoon = useCallback(() => {
    if (!interactive) return close();
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(close, 250);
  }, [close, interactive]);
  const keepOpen = useCallback(() => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  }, []);
  useEffect(() => () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  }, []);

  // Escape dismisses it for keyboard users, who opened it by focusing the chip
  // and would otherwise have to tab away to get rid of it.
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLSpanElement>) => {
      if (e.key === 'Escape') close();
    },
    [close],
  );

  // A tooltip pinned to a rect that has since moved is worse than no tooltip,
  // so any scroll or resize dismisses it rather than stranding it mid-page.
  useEffect(() => {
    if (!tip) return;
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [tip, close]);

  const indicator = (
    <span
      ref={ref}
      className="health"
      data-health={health}
      data-has-reason={text ? 'true' : undefined}
      data-labelled={label ? 'true' : 'false'}
      style={{ ['--health-color' as string]: healthToken(health) }}
      tabIndex={text ? 0 : undefined}
      aria-describedby={text && tip ? tipId : undefined}
      onMouseEnter={open}
      onMouseLeave={closeSoon}
      onFocus={open}
      onBlur={closeSoon}
      onKeyDown={handleKeyDown}
    >
      <span className="health-dot" />
      {label && <span>{health}</span>}
    </span>
  );

  if (!text) return indicator;

  return (
    <>
      {indicator}
      {tip &&
        createPortal(
          <div
            id={tipId}
            role="tooltip"
            className="tooltip health-tooltip"
            data-interactive={interactive ? 'true' : undefined}
            style={{ left: tip.x, top: tip.y, transform: tip.flip ? 'translateY(-100%)' : undefined }}
            onMouseEnter={interactive ? keepOpen : undefined}
            onMouseLeave={interactive ? closeSoon : undefined}
            onFocus={interactive ? keepOpen : undefined}
            onBlur={interactive ? closeSoon : undefined}
            // React bubbles portal events up the component tree, so without
            // this a click in here would also fire the row link or map node
            // the chip sits in.
            onClick={interactive ? (e) => e.stopPropagation() : undefined}
            onMouseDown={interactive ? (e) => e.stopPropagation() : undefined}
            onKeyDown={interactive ? (e) => e.stopPropagation() : undefined}
          >
            <div className="tooltip-title health-tooltip-title">{health}</div>
            {interactive ? body : text}
          </div>,
          document.body,
        )}
    </>
  );
}

export function Badge({
  children,
  intent,
}: {
  children: ReactNode;
  intent?: 'neutral' | 'good' | 'warn' | 'bad' | 'info';
}) {
  return (
    <span className="badge" data-intent={intent ?? 'neutral'}>
      {children}
    </span>
  );
}

/** Maps free-text states from plugins onto a visual intent. */
export function autoIntent(value: string): 'neutral' | 'good' | 'warn' | 'bad' | 'info' {
  const v = value.toLowerCase();
  if (/(fail|down|critical|error|breach|blocker|expired|suspended|unregistered|urgent|compromis)/.test(v)) return 'bad';
  if (/(warn|degraded|pending|low|stale|overdue|high|queued|on_hold)/.test(v)) return 'warn';
  if (/(success|ok|up|resolved|registered|active|answered|verified|good|closed)/.test(v)) return 'good';
  if (/(running|new|open|info|in use|inbound|outbound)/.test(v)) return 'info';
  return 'neutral';
}

/* ------------------------------------------------------------------ */
/* Feedback states                                                     */
/* ------------------------------------------------------------------ */

export function EmptyState({
  icon,
  title,
  text,
  action,
}: {
  icon?: ReactNode;
  title: string;
  text?: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty">
      <div className="empty-icon">{icon ?? <Inbox size={20} />}</div>
      <div className="empty-title">{title}</div>
      {text && <div className="empty-text">{text}</div>}
      {action}
    </div>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="row" style={{ gap: 8, color: 'var(--itr-text-subtle)', fontSize: 13 }}>
      <span className="spinner" />
      {label && <span>{label}</span>}
    </div>
  );
}

/** Skeleton shaped like the content it replaces, so nothing shifts on load. */
export function Skeleton({ height = 16, width = '100%', style }: { height?: number | string; width?: number | string; style?: React.CSSProperties }) {
  return <div className="skeleton" style={{ height, width, ...style }} />;
}

export function SkeletonTable({ rows = 5, cols = 4 }: { rows?: number; cols?: number }) {
  return (
    <div className="col" style={{ gap: 9, padding: '4px 0' }}>
      {Array.from({ length: rows }, (_, r) => (
        <div key={r} className="row" style={{ gap: 12 }}>
          {Array.from({ length: cols }, (_, c) => (
            <Skeleton key={c} height={13} width={c === 0 ? '32%' : `${Math.round(68 / (cols - 1))}%`} />
          ))}
        </div>
      ))}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: string; onRetry?: () => void }) {
  return (
    <EmptyState
      icon={<XCircle size={20} style={{ color: 'var(--itr-critical)' }} />}
      title="Could not load this"
      text={error}
      action={
        onRetry && (
          <button className="btn btn-sm" onClick={onRetry}>
            Try again
          </button>
        )
      }
    />
  );
}

/**
 * Catches a render crash in one page so the rest of the app stays up.
 *
 * Without this, a single `undefined.toFixed()` in a page unmounts the whole
 * React root and the operator sees a blank window with nothing to report.
 * Shown instead: the error text, which is what we need to fix it. Mount it
 * with `key={location.pathname}` so navigating away gives the next page a
 * clean start.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  override state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error): { error: Error } {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Page crashed', error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <EmptyState
        icon={<XCircle size={20} style={{ color: 'var(--itr-critical)' }} />}
        title="This page hit an error"
        text={`${error.name}: ${error.message}`}
        action={
          <button className="btn btn-sm" onClick={() => this.setState({ error: null })}>
            Try again
          </button>
        }
      />
    );
  }
}

/* ------------------------------------------------------------------ */
/* Toasts                                                              */
/* ------------------------------------------------------------------ */

interface Toast {
  id: number;
  message: string;
  intent: 'good' | 'warn' | 'bad' | 'info';
  detail?: string;
}

const ToastContext = createContext<{
  toast: (message: string, intent?: Toast['intent'], detail?: string) => void;
} | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const toast = useCallback((message: string, intent: Toast['intent'] = 'info', detail?: string) => {
    const id = nextId.current++;
    setToasts((current) => [...current, { id, message, intent, detail }]);
    // Errors linger; confirmations get out of the way.
    const ttl = intent === 'bad' ? 8000 : 4000;
    setTimeout(() => setToasts((current) => current.filter((t) => t.id !== id)), ttl);
  }, []);

  const dismiss = (id: number) => setToasts((current) => current.filter((t) => t.id !== id));

  const value = useMemo(() => ({ toast }), [toast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className="toast" data-intent={t.intent}>
            <span style={{ flexShrink: 0, marginTop: 1 }}>
              {t.intent === 'bad' ? (
                <XCircle size={15} style={{ color: 'var(--itr-critical)' }} />
              ) : t.intent === 'warn' ? (
                <AlertTriangle size={15} style={{ color: 'var(--itr-warning)' }} />
              ) : t.intent === 'good' ? (
                <CheckCircle2 size={15} style={{ color: 'var(--itr-success)' }} />
              ) : (
                <Info size={15} style={{ color: 'var(--itr-info)' }} />
              )}
            </span>
            <div className="grow">
              <div>{t.message}</div>
              {t.detail && <div className="subtle" style={{ fontSize: 12, marginTop: 2 }}>{t.detail}</div>}
            </div>
            <button className="btn btn-ghost btn-icon btn-sm" onClick={() => dismiss(t.id)} aria-label="Dismiss">
              <X size={13} />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx.toast;
}

/* ------------------------------------------------------------------ */
/* Drawer                                                              */
/* ------------------------------------------------------------------ */

export function Drawer({
  open,
  onClose,
  title,
  children,
  footer,
  wide,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /** Wider variant for content that needs more than the default 560px — a chat transcript, a config diff. */
  wide?: boolean;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    // Freeze the page behind the drawer so a trackpad flick does not scroll it.
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <div className="overlay" onClick={onClose} />
      <aside className={`drawer${wide ? ' wide' : ''}`} role="dialog" aria-modal="true" aria-label={typeof title === 'string' ? title : undefined}>
        <header className="drawer-header">
          <div className="grow" style={{ fontSize: 15, fontWeight: 620, letterSpacing: '-0.012em' }}>
            {title}
          </div>
          <button className="btn btn-ghost btn-icon" onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </header>
        <div className="drawer-body">{children}</div>
        {footer && <footer className="drawer-footer">{footer}</footer>}
      </aside>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Data fetching                                                       */
/* ------------------------------------------------------------------ */

/**
 * Minimal fetch hook with polling.
 *
 * Deliberately not a query library: the app has one refresh model (poll, or
 * subscribe over the websocket), and this keeps the loading/error/refetch shape
 * identical on every page.
 */
export function useFetch<T>(
  fetcher: () => Promise<T>,
  deps: unknown[] = [],
  options: { refreshMs?: number; enabled?: boolean } = {},
): { data: T | undefined; loading: boolean; error: string | null; reload: () => void } {
  const [data, setData] = useState<T>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const enabled = options.enabled !== false;

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    let cancelled = false;

    const run = async (showSpinner: boolean) => {
      if (showSpinner) setLoading(true);
      try {
        const result = await fetcherRef.current();
        if (!cancelled) {
          setData(result);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void run(true);

    // Background refreshes never show the spinner — a dashboard that flashes
    // skeletons every 15 seconds is unusable.
    let timer: number | undefined;
    if (options.refreshMs) {
      timer = window.setInterval(() => {
        if (document.visibilityState === 'visible') void run(false);
      }, options.refreshMs);
    }

    return () => {
      cancelled = true;
      if (timer) window.clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce, enabled, options.refreshMs]);

  return { data, loading, error, reload: () => setNonce((n) => n + 1) };
}

/** Debounce a value — used for search inputs so we do not query per keystroke. */
export function useDebounced<T>(value: T, delayMs = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

/* ------------------------------------------------------------------ */
/* Multi-select                                                        */
/* ------------------------------------------------------------------ */

/**
 * Checkbox-row selection for a list/table, shared across every page that
 * offers bulk actions.
 *
 * Selection is scoped to whatever `ids` the caller passes on each render —
 * `count`/`selectedIds` only ever include ids currently in that list. A
 * filter change that hides a previously-picked row does not leave a stale
 * "3 selected" toolbar pointing at rows no longer on screen; picking under
 * one filter and switching to another simply hides that pick from the count
 * rather than discarding it, so switching back restores it.
 */
export interface Selection {
  selectedIds: string[];
  isSelected: (id: string) => boolean;
  toggle: (id: string) => void;
  toggleAll: () => void;
  clear: () => void;
  allSelected: boolean;
  count: number;
}

export function useSelection(ids: string[]): Selection {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const toggle = useCallback((id: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelected((current) => {
      const allOn = ids.length > 0 && ids.every((id) => current.has(id));
      const next = new Set(current);
      for (const id of ids) (allOn ? next.delete(id) : next.add(id));
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids]);

  const clear = useCallback(() => setSelected(new Set()), []);

  const selectedIds = useMemo(() => ids.filter((id) => selected.has(id)), [ids, selected]);

  return {
    selectedIds,
    isSelected: (id: string) => selected.has(id),
    toggle,
    toggleAll,
    clear,
    allSelected: ids.length > 0 && ids.every((id) => selected.has(id)),
    count: selectedIds.length,
  };
}

/** Header checkbox with the indeterminate (some-but-not-all) state React
 *  cannot express as a plain attribute — it must be set on the DOM node.
 *  Wrapped in a padded `<label>` (native label-click toggles the input for
 *  free) so the clickable area is bigger than the checkbox glyph itself —
 *  the negative margin lets that padding overlap the cell's own without
 *  pushing the header text over. */
export function SelectAllCheckbox({ selection }: { selection: Selection }) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = selection.count > 0 && !selection.allSelected;
  }, [selection.count, selection.allSelected]);

  return (
    <label className="select-cell" onClick={(e) => e.stopPropagation()}>
      <input
        ref={ref}
        type="checkbox"
        checked={selection.allSelected}
        onChange={selection.toggleAll}
        aria-label="Select all"
      />
    </label>
  );
}

/**
 * A row's own checkbox. Stops the click from also firing the row's own
 * `onClick` navigation, which every one of these tables has on the `<tr>` —
 * on the wrapping `<label>`, not just the `<input>`, so a click anywhere in
 * the padded target (not only the tiny glyph itself) both toggles the row
 * *and* never navigates away and drops the selection.
 */
export function RowCheckbox({ selection, id }: { selection: Selection; id: string }) {
  return (
    <label className="select-cell" onClick={(e) => e.stopPropagation()}>
      <input
        type="checkbox"
        checked={selection.isSelected(id)}
        onChange={() => selection.toggle(id)}
        aria-label="Select row"
      />
    </label>
  );
}

/**
 * The bulk-action toolbar that appears once something is selected. Renders
 * nothing at zero — callers do not need their own `count > 0` guard.
 */
export function SelectionBar({ selection, children }: { selection: Selection; children: ReactNode }) {
  if (selection.count === 0) return null;
  return (
    <div
      className="row"
      style={{
        gap: 10,
        marginBottom: 12,
        padding: '8px 12px',
        background: 'var(--itr-surface-sunken)',
        border: 'var(--itr-border-w) solid var(--itr-border)',
        borderRadius: 'var(--itr-radius-sm)',
      }}
    >
      <span style={{ fontSize: 12.5, fontWeight: 600 }}>
        {selection.count} selected
      </span>
      <span className="grow" />
      {children}
      <button className="btn btn-sm btn-ghost" onClick={selection.clear}>
        Clear
      </button>
    </div>
  );
}

/**
 * Runs an action across every selected id in parallel and tallies the
 * outcome — a partial failure (one device unreachable, one draft-only
 * invoice among issued ones) surfaces as a count rather than aborting or
 * silently swallowing the rest.
 */
export async function runBulk(ids: string[], action: (id: string) => Promise<unknown>): Promise<{ ok: number; failed: number }> {
  const results = await Promise.allSettled(ids.map(action));
  const ok = results.filter((r) => r.status === 'fulfilled').length;
  return { ok, failed: results.length - ok };
}

/** Standard toast for a `runBulk` result — "Deleted 5" or "Deleted 3, 2 failed". */
export function bulkResultMessage(verb: string, result: { ok: number; failed: number }): { message: string; intent: 'good' | 'warn' } {
  if (result.failed === 0) return { message: `${verb} ${result.ok}`, intent: 'good' };
  return { message: `${verb} ${result.ok}, ${result.failed} failed`, intent: 'warn' };
}
